import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useMemo, useState } from 'react';
import format from 'date-fns/format';
import getYear from 'date-fns/getYear';
import getMonth from 'date-fns/getMonth';
import parseISO from 'date-fns/parseISO';
import { useDesignContext } from '@klarna/designtokens';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import Chip from '@bubble-ui/molecule-chip';
import View from '@bubble-ui/primitive-view';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import usePropDeprecationWarning from '@bubble-ui/hook-use-prop-deprecation-warning';
import { DatePickerDay } from '../Day';
import { defaultDesignTokens } from '../defaultDesignTokens';
import { DatePickerMonth } from '../Month';
import { getNextMonth, getPreviousMonth } from '../utils';
import { buildIso, dateToMonthArray, presetSelected, rangeEnd, rangeSelected, rangeStart, selected, withinRange } from './utils';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
var dayToIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
var DatePicker = function DatePicker(_ref) {
  var currentYear = _ref.currentYear,
    description = _ref.description,
    disableDatesHandler = _ref.disableDatesHandler,
    doubleMonth = _ref.doubleMonth,
    dueDate = _ref.dueDate,
    _ref$firstDayOfWeek = _ref.firstDayOfWeek,
    firstDayOfWeek = _ref$firstDayOfWeek === void 0 ? 1 : _ref$firstDayOfWeek,
    id = _ref.id,
    _ref$inline = _ref.inline,
    inline = _ref$inline === void 0 ? false : _ref$inline,
    isTodayShown = _ref.isTodayShown,
    monthCurrentlyShown = _ref.monthCurrentlyShown,
    monthNames = _ref.monthNames,
    nextMonthLabel = _ref.nextMonthLabel,
    onChange = _ref.onChange,
    onGoToNextMonth = _ref.onGoToNextMonth,
    onGoToPreviousMonth = _ref.onGoToPreviousMonth,
    presets = _ref.presets,
    previousMonthLabel = _ref.previousMonthLabel,
    rangeSelection = _ref.rangeSelection,
    value = _ref.value,
    _ref$warning = _ref.warning,
    warning = _ref$warning === void 0 ? false : _ref$warning,
    weekDayOneLetterNames = _ref.weekDayOneLetterNames;
  usePropDeprecationWarning({
    componentName: 'DatePicker',
    propName: 'inline',
    shouldWarn: inline !== undefined,
    removalDate: 'v6.0.0'
  });
  var designVersion = useDesignVersion();
  var designContext = useDesignContext(defaultDesignTokens);
  var _getDefaultStyleSheet = getDefaultStyleSheet({
      designContext: designContext,
      designVersion: designVersion,
      doubleMonth: doubleMonth,
      inline: inline,
      presets: presets
    }),
    monthStyle = _getDefaultStyleSheet.monthStyle,
    weekDayStyle = _getDefaultStyleSheet.weekDayStyle,
    monthDayStyle = _getDefaultStyleSheet.monthDayStyle,
    rootStyle = _getDefaultStyleSheet.rootStyle,
    calendarContainerStyle = _getDefaultStyleSheet.calendarContainerStyle,
    presetContainerStyle = _getDefaultStyleSheet.presetContainerStyle,
    spacerVerticalExtraSmallStyle = _getDefaultStyleSheet.spacerVerticalExtraSmallStyle;
  var ids = getIds(id);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    hoveredDay = _useState2[0],
    setHoveredDay = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    pressed = _useState4[0],
    setPressed = _useState4[1];
  var weekDayOneLetterNamesArray = useMemo(function () {
    return dayToIndex.map(function (_, index) {
      return dayToIndex[(index + firstDayOfWeek) % 7];
    }).map(function (dayName) {
      return weekDayOneLetterNames[dayName];
    });
  }, [firstDayOfWeek, weekDayOneLetterNames]);
  var _useMemo = useMemo(function () {
      var shownMonth = parseInt(monthCurrentlyShown.slice(5, 7)) - 1;
      var shownYear = parseInt(monthCurrentlyShown.slice(0, 4));
      if (rangeSelection === true) {
        var startDateObject = value && value[0] && parseISO(value[0]);
        return {
          monthArray: dateToMonthArray({
            shownMonth: shownMonth,
            shownYear: shownYear,
            firstDayOfWeek: firstDayOfWeek
          }),
          startValueYear: startDateObject && getYear(startDateObject),
          startValueMonth: startDateObject && getMonth(startDateObject) + 1,
          shownMonth: shownMonth,
          shownYear: shownYear
        };
      }
      return {
        monthArray: dateToMonthArray({
          shownMonth: shownMonth,
          shownYear: shownYear,
          firstDayOfWeek: firstDayOfWeek
        }),
        shownMonth: shownMonth,
        shownYear: shownYear
      };
    }, [firstDayOfWeek, monthCurrentlyShown, rangeSelection, value]),
    monthArray = _useMemo.monthArray,
    startValueYear = _useMemo.startValueYear,
    startValueMonth = _useMemo.startValueMonth,
    shownMonth = _useMemo.shownMonth,
    shownYear = _useMemo.shownYear;
  var hoveredDate = buildIso(shownYear, shownMonth, hoveredDay);
  var onDayHovered = function onDayHovered(value) {
    return value && setHoveredDay(value.toString());
  };
  var onPress = function onPress() {
    return setPressed(true);
  };
  var onRelease = function onRelease() {
    return setPressed(false);
  };
  return /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    style: rootStyle
  }, /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.calendarContainer,
    style: calendarContainerStyle
  }, /*#__PURE__*/React.createElement(DatePickerMonth, {
    id: ids === null || ids === void 0 ? void 0 : ids.monthPicker,
    description: description,
    inline: inline,
    label: currentYear && parseInt(currentYear) === shownYear ? monthNames[shownMonth] : "".concat(monthNames[shownMonth], " ").concat(shownYear),
    backLabel: previousMonthLabel,
    forwardLabel: nextMonthLabel,
    onGoToNextMonth: onGoToNextMonth ? function () {
      return onGoToNextMonth(getNextMonth(shownMonth, shownYear));
    } : undefined,
    onGoToPreviousMonth: onGoToPreviousMonth ? function () {
      return onGoToPreviousMonth(getPreviousMonth(shownMonth, shownYear));
    } : undefined,
    warning: warning
  }), /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.daysContainer,
    style: monthStyle
  }, /*#__PURE__*/React.createElement(View, {
    style: weekDayStyle
  }, weekDayOneLetterNamesArray.map(function (weekDayOneLetterName, index) {
    return /*#__PURE__*/React.createElement(DatePickerDay, {
      id: ids === null || ids === void 0 ? void 0 : ids.weekDay(index),
      inline: inline,
      isDayLabel: true,
      label: weekDayOneLetterName,
      key: index
    });
  })), monthArray.map(function (mWeek, index) {
    return /*#__PURE__*/React.createElement(View, {
      style: monthDayStyle,
      key: index
    }, mWeek.map(function (mDay, index) {
      var current = mDay ? buildIso(shownYear, shownMonth, mDay) : undefined;
      return /*#__PURE__*/React.createElement(DatePickerDay, {
        id: ids === null || ids === void 0 ? void 0 : ids.monthDay(index),
        inline: inline,
        dueDate: current ? dueDate === current : undefined,
        disabled: disableDatesHandler && current ? disableDatesHandler(current) : undefined,
        isToday: isTodayShown && format(new Date(), 'yyyy-MM-dd') === current,
        selected: selected({
          current: current,
          rangeSelection: rangeSelection,
          value: value
        }),
        rangeStart: rangeStart({
          current: current,
          hoveredDate: hoveredDate,
          rangeSelection: rangeSelection,
          value: value
        }),
        rangeEnd: rangeEnd({
          current: current,
          hoveredDate: hoveredDate,
          hoveredDay: hoveredDay,
          mDay: mDay,
          rangeSelection: rangeSelection,
          shownMonth: shownMonth,
          shownYear: shownYear,
          startValueMonth: startValueMonth,
          startValueYear: startValueYear,
          value: value
        }),
        withinRange: withinRange({
          current: current,
          hoveredDate: hoveredDate,
          rangeSelection: rangeSelection,
          value: value
        }),
        rangeSelected: rangeSelected({
          current: current,
          rangeSelection: rangeSelection,
          value: value
        }),
        pressed: rangeSelection && (value === null || value === void 0 ? void 0 : value.length) === 1 && pressed && (withinRange({
          current: current,
          hoveredDate: hoveredDate,
          rangeSelection: rangeSelection,
          value: value
        }) || rangeEnd({
          current: current,
          hoveredDate: hoveredDate,
          hoveredDay: hoveredDay,
          mDay: mDay,
          rangeSelection: rangeSelection,
          shownMonth: shownMonth,
          shownYear: shownYear,
          startValueMonth: startValueMonth,
          startValueYear: startValueYear,
          value: value
        }) || rangeStart({
          current: current,
          hoveredDate: hoveredDate,
          rangeSelection: rangeSelection,
          value: value
        })),
        onFocus: function onFocus() {
          return mDay && onDayHovered(mDay);
        },
        onMouseOver: function onMouseOver() {
          return mDay && onDayHovered(mDay);
        },
        onMouseDown: onPress,
        onMouseUp: onRelease,
        onClick: function onClick() {
          if (rangeSelection) {
            if ((value === null || value === void 0 ? void 0 : value.length) === 2 || (value === null || value === void 0 ? void 0 : value.length) === 0) {
              onChange([current]);
            } else if (current && value && value[0] && current > value[0]) {
              onChange([value[0], current]);
            } else {
              value && value[0] && onChange([current, value[0]]);
            }
          } else {
            current && onChange(current);
          }
        },
        label: mDay,
        key: index
      });
    }));
  }))), presets && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.presetContainer,
    style: presetContainerStyle
  }, presets.map(function (preset, index) {
    return /*#__PURE__*/React.createElement(View, {
      key: index
    }, /*#__PURE__*/React.createElement(Chip, {
      title: preset.label,
      onPress: function onPress() {
        return onChange([preset.range.startDate, preset.range.endDate]);
      },
      isActive: presetSelected({
        preset: preset,
        value: value
      })
    }), presets.length - 1 !== index && /*#__PURE__*/React.createElement(View, {
      style: spacerVerticalExtraSmallStyle
    }));
  })));
};
DatePicker.displayName = 'DatePicker';
export var DatePickerMain = withProvisionalOverrideFromContext(DatePicker);
//# sourceMappingURL=index.js.map